if (!customElements.get('search-bar')) {
  class SearchBar extends HTMLElement {
    searchTimeout = null
    abortController = new AbortController()

    constructor() {
      super()
      this.searchInput = this.querySelector('#searchbar__input')
      this.searchResultsContainer = this.querySelector('#searchResults')
      this.closeSearchButton = this.querySelector('#nav__search-close')
    }

    connectedCallback() {
      this.searchInput.addEventListener('input', this.handleSearchInput.bind(this))
      this.closeSearchButton.addEventListener('click', this.closeSearch.bind(this))

      // Add keydown event listener for Escape key
      document.addEventListener('keydown', this.handleKeydown.bind(this))

      // Add click event listener for clicks outside search bar
      document.addEventListener('click', this.handleClickOutside.bind(this))
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.handleKeydown.bind(this))
      document.removeEventListener('click', this.handleClickOutside.bind(this))
    }

    openSearch() {
      this.classList.add('active')
      this.setAttribute('aria-hidden', 'false')
      this.searchInput.value = ''
      this.searchInput.focus()
      document.body.classList.add('overflow-hidden')
    }

    closeSearch() {
      const navWrapper = document.querySelector('.shopify-section.main-nav__wrapper')
      const announcementBar = document.querySelector(
        '.shopify-section.new-announcement-bar__wrapper',
      )
      navWrapper.classList.add('is-sticky')
      announcementBar.classList.add('is-sticky')
      this.classList.remove('active')
      this.setAttribute('aria-hidden', 'true')
      this.clearSearchResults()
      document.body.classList.remove('overflow-hidden')
    }

    handleSearchInput(event) {
      const query = event.target.value.trim()

      this.abortController.abort()
      this.abortController = new AbortController()

      // Clear previous timeout
      clearTimeout(this.searchTimeout)

      // Only search if query has at least 2 characters
      if (query.length < 2) {
        this.clearSearchResults()
        return
      }

      // Debounce the search to avoid too many requests
      this.searchTimeout = setTimeout(async () => {
        const resultsHTML = await this.fetchPredictiveSearchSection(query)
        this.displaySearchResults(resultsHTML)
      }, 300)
    }

    async fetchPredictiveSearchSection(query) {
      try {
        // Construct the URL using Shopify's global routes object
        const url = `${window.Shopify.routes.root}search/suggest?q=${encodeURIComponent(query)}&resources[type]=product&section_id=predictive-search`

        const response = await fetch(url, { signal: this.abortController.signal })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const html = await response.text()
        return html
      } catch (error) {
        console.error('Error fetching predictive search section:', error)
        return null
      }
    }

    displaySearchResults(resultsHTML) {
      this.clearSearchResults()
      if (!resultsHTML) return

      const updateResults = () => {
        const parsedDoc = new DOMParser().parseFromString(resultsHTML, 'text/html')
        const section = parsedDoc.querySelector('#shopify-section-predictive-search')

        if (section) {
          // Filter out products with 'hidden from search' tag
          const productItems = section.querySelectorAll('.predictive-search__item')
          productItems.forEach((item) => {
            const tags = item.dataset.tags || ''
            if (tags.toLowerCase().includes('hidden from search')) {
              item.remove()
            }
          })

          this.searchResultsContainer.innerHTML = section.innerHTML
        }
      }

      // Use View Transitions API if supported, otherwise fallback
      document.startViewTransition?.(updateResults) ?? updateResults()
    }

    handleKeydown(event) {
      if (event.key === 'Escape' && this.classList.contains('active')) {
        this.closeSearch()
        document.querySelector('#nav__tab--search').focus()
      }
    }

    handleClickOutside(event) {
      const mainNav = document.querySelector('main-nav')
      const panel = this.querySelector('.search-bar__search-panel-container')
      const isSearchActive = this.classList.contains('active')
      const searchResults = this.querySelector('.predictive-search-results-container')

      const isOutsidePanel =
        isSearchActive && !panel.contains(event.target) && !mainNav.contains(event.target)

      if (isOutsidePanel && !searchResults?.contains(event.target)) {
        this.closeSearch()
      }
    }

    clearSearchResults() {
      const clearContainer = () => {
        this.searchResultsContainer.innerHTML = ''
      }

      // Use View Transitions API if supported, otherwise fallback
      document.startViewTransition?.(clearContainer) ?? clearContainer()
    }
  }

  customElements.define('search-bar', SearchBar)
}
